import { Button, Loading, Overlay, useRefAutoUpdate, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useEffect, useRef, useState } from "react";
import styles from "./ConnexionView.module.css";
import { LoadingView } from "./LoadingView";

export interface ConnexionViewProps extends WithNativeHTMLProps<HTMLDivElement> {
  status: "connecting" | "connected" | "disconnected";
  hide?: boolean;
  retry?: () => void;
  retryAuto?: boolean;
  retryAfter?: number; // seconds
  className?: string;
  style?: React.CSSProperties;
}

export interface ConnexionViewRef {}

const ConnexionView = React.forwardRef(
  (
    { status, hide = false, retry, retryAuto = true, retryAfter = 15, className, style, ...nativeProps }: ConnexionViewProps,
    forwardRef: ForwardedRef<ConnexionViewRef | undefined>
  ) => {
    const firstConnectionRef = useRef<boolean>(true);
    const firstTryConnectionRef = useRef<boolean>(true);
    const [counterRetry, setCounterRetry] = useState<number>(-1);
    const retryAutoRef = useRefAutoUpdate(retryAuto);
    const retryAfterRef = useRefAutoUpdate(retryAfter);
    const retryRef = useRefAutoUpdate(retry);

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const retryConnectSocket = () => {
      if (!retryRef.current) return;
      setCounterRetry(-1);
      retryRef.current();
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFETS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (status !== "connected") firstTryConnectionRef.current = false;
      if (status === "connected") {
        firstConnectionRef.current = false;
      } else if (status === "disconnected") {
        if (retryAutoRef.current) setCounterRetry(retryAfterRef.current);
      }
    }, [status]);

    useEffect(() => {
      if (status === "disconnected") {
        if (counterRetry > 0) {
          const timeout = setTimeout(() => {
            setCounterRetry(counterRetry - 1);
          }, 1000);
          return () => clearTimeout(timeout);
        } else if (counterRetry === 0) {
          retryConnectSocket();
        }
      }
    }, [counterRetry, status]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (status === "connected" || hide) return null;

    const renderContent = () => {
      if (status === "connecting") {
        return <LoadingView message="Connexion en cours" />;
      }
      if (status === "disconnected") {
        return (
          <div className={styles.message}>
            <div className={styles.errorDescription}>{"Une erreur est survenue durant la connexion.\nVérifiez votre connexion internet."}</div>
            {counterRetry > 0 ? <div className={styles.errorRetry}>{`Reconnexion dans ${counterRetry} secondes...`}</div> : null}
            <Button className={styles.retryButton} title={counterRetry > 0 ? "Réessayer maintenant" : "Réessayer"} onPressOut={() => retryConnectSocket()} />
          </div>
        );
      }
      return null;
    };

    const classes = [styles.container];
    if (className) classes.push(className);
    return (
      <Overlay className={classes.join(" ")} style={style} {...nativeProps}>
        {renderContent()}
      </Overlay>
    );
  }
);

export default ConnexionView;
